import { fs } from '@/theme/tokens';
import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/Lock';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  useSourceMapping, useUpdateSource,
  type RemoteField, type SyncSource, type TargetKind } from '@/api/hrms-sync';
import { errorMessage } from '@/lib/api-client';
import { fmtWhen, matchesQuery } from './format';
import { ConfidenceChip, FieldSearchBox } from './shared';

/**
 * Match one uKnowva source's columns to a payroll master's fields.
 *
 * The left column is payroll's vocabulary, the dropdown is the instance's own
 * column names (with sample values, so "which one holds the shift start?" can
 * be answered by looking). Suggestions are pre-filled with their confidence;
 * nothing is imported until "Save & confirm".
 */
export function SourceMappingPanel({
  source, kinds, canEdit, onSaved }: {
  source: SyncSource;
  kinds: TargetKind[];
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const [kind, setKind] = useState<string>(source.target_kind ?? '');
  const previewKind = kind && kind !== source.target_kind ? kind : undefined;
  const { data, isLoading, error, refetch } = useSourceMapping(source.source_key, previewKind);
  const update = useUpdateSource();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  // Mounted once per source (the parent keys it), so only a kind changed
  // from elsewhere — the Sources tab's dropdown — needs following here.
  useEffect(() => { setKind(source.target_kind ?? ''); }, [source.target_kind]);

  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const f of data.fields) if (f.remote) next[f.name] = f.remote;
    setDraft(next);
  }, [data]);

  const remoteByKey = useMemo(
    () => Object.fromEntries((data?.remote_fields ?? []).map((r) => [r.key, r])),
    [data],
  );

  const used = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(draft).forEach((v) => { if (v) counts[v] = (counts[v] ?? 0) + 1; });
    return counts;
  }, [draft]);

  const missingRequired = (data?.fields ?? [])
    .filter((f) => f.required && !draft[f.name])
    .map((f) => f.label);
  const duplicates = Object.entries(used).filter(([, n]) => n > 1).map(([k]) => k);
  const dirty = data ? data.fields.some((f) => (f.remote ?? '') !== (draft[f.name] ?? '')) : false;

  const save = async (confirm: boolean) => {
    setSaved(null);
    try {
      await update.mutateAsync({
        sourceKey: source.source_key,
        target_kind: kind || null,
        column_map: kind ? draft : {},
        confirm: kind ? confirm : false });
      setSaved(confirm ? 'Mapping confirmed. The next sync imports this data into payroll.'
        : kind ? 'Draft saved. It is not used for imports until you confirm it.'
          : 'Saved. This source is stored only.');
      onSaved?.();
      void refetch();
    } catch {
      /* shown below */
    }
  };

  const applySuggestions = () => {
    if (!data) return;
    const next = { ...draft };
    for (const f of data.fields) if (f.suggested && !next[f.name]) next[f.name] = f.suggested;
    setDraft(next);
  };

  const kindDef = kinds.find((k) => k.key === kind);

  // View filter only: a payroll field matches on its label, code or note, or
  // on the uKnowva column currently mapped to it. Hidden rows keep their
  // draft and are still saved.
  const visibleFields = (data?.fields ?? []).filter((f) => {
    const chosen = draft[f.name] ?? '';
    const r = chosen ? remoteByKey[chosen] : undefined;
    return matchesQuery(query, f.label, f.name, f.note, chosen, r?.label);
  });

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Import into" value={kind}
          onChange={(e) => setKind(e.target.value)}
          sx={{ minWidth: 240 }} disabled={!canEdit || source.is_core}
          helperText={source.is_core ? 'Core sources have their own import' : undefined}
        >
          <MenuItem value=""><em>Store only (no payroll import)</em></MenuItem>
          {kinds.map((k) => (
            <MenuItem key={k.key} value={k.key}>
              {k.label}{source.suggested_kind === k.key ? '  · suggested' : ''}
            </MenuItem>
          ))}
        </TextField>
        {data?.confirmed && (
          <Chip color="success" size="small"
            label={`Confirmed ${fmtWhen(data.confirmed_at)}${data.confirmed_by ? ` by ${data.confirmed_by}` : ''}`} />
        )}
        {data && !data.confirmed && kind && (
          <Chip color={data.auto_ready ? 'info' : 'warning'} size="small"
            label={data.auto_ready ? 'All required columns auto-matched' : 'Needs your mapping'} />
        )}
      </Stack>

      {kindDef?.note && <Alert severity="info" sx={{ mb: 2 }}>{kindDef.note}</Alert>}
      {isLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
      {error && <Alert severity="error">{errorMessage(error)}</Alert>}

      {data && kind && (
        <>
          {data.fields.length > 0 && (
            <FieldSearchBox value={query} onChange={setQuery}
              shown={visibleFields.length} total={data.fields.length} />
          )}
          <TableContainer><Table size="small" sx={{ '& td, & th': { py: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '28%' }}>Payroll field</TableCell>
                <TableCell sx={{ width: '34%' }}>uKnowva column</TableCell>
                <TableCell>Sample values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFields.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No field matches “{query.trim()}”.
                  </TableCell>
                </TableRow>
              )}
              {visibleFields.map((f) => {
                const chosen = draft[f.name] ?? '';
                const remote = chosen ? remoteByKey[chosen] : undefined;
                const isDup = Boolean(chosen) && (used[chosen] ?? 0) > 1;
                const changedFromSaved = (f.remote ?? '') !== chosen;
                return (
                  <TableRow key={f.name} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight={f.required ? 700 : 500}>
                          {f.label}{f.required && <Box component="span" color="error.main"> *</Box>}
                        </Typography>
                        {f.protected && (
                          <Tooltip title="Set when the row is created and never overwritten by a sync. It affects what people are paid.">
                            <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      {f.note && (
                        <Typography variant="caption" color="text.secondary">{f.note}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RemoteColumnPicker
                          value={chosen}
                          options={data.remote_fields}
                          suggested={f.suggested}
                          disabled={!canEdit}
                          error={Boolean(isDup) || (f.required && !chosen)}
                          onChange={(key) => setDraft({ ...draft, [f.name]: key })}
                        />
                        {!changedFromSaved && <ConfidenceChip value={f.confidence} />}
                      </Stack>
                      {isDup && (
                        <Typography variant="caption" color="error">Also mapped to another field</Typography>
                      )}
                      {!chosen && f.name === 'code' && data.code_from_record_id && (
                        <Typography variant="caption" color="text.secondary">
                          Falls back to the uKnowva record id
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {(remote?.samples ?? []).join(' · ') || (chosen ? 'no values stored yet' : '')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></TableContainer>

          {(missingRequired.length > 0 || duplicates.length > 0) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {missingRequired.length > 0 && <>Required: {missingRequired.join(', ')}. </>}
              {duplicates.length > 0 && <>Used twice: {duplicates.join(', ')}.</>}
            </Alert>
          )}
          {data.remote_fields.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              uKnowva did not describe this source's columns. Run a sync once. The columns are
              read from the records themselves. Then map them here.
            </Alert>
          )}
        </>
      )}

      {update.error && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(update.error)}</Alert>}
      {saved && <Alert severity="success" sx={{ mt: 2 }}>{saved}</Alert>}

      {canEdit && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          {kind && (
            <Button startIcon={<AutoFixHighIcon />} onClick={applySuggestions} disabled={!data}>
              Fill suggestions
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {kind ? (
            <>
              <Button variant="outlined" onClick={() => save(false)}
                disabled={update.isPending || (!dirty && kind === (source.target_kind ?? ''))}>
                Save draft
              </Button>
              <Button variant="contained" onClick={() => save(true)}
                disabled={update.isPending || missingRequired.length > 0 || duplicates.length > 0}>
                {update.isPending ? 'Saving…' : 'Save & confirm'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => save(false)}
              disabled={update.isPending || !source.target_kind}>
              Store only
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Type-to-search over a source's columns: label, key, type and sample values,
// so "which one holds the shift start?" is answered by typing "09:30".
const filterRemote = createFilterOptions<RemoteField>({
  stringify: (r) => `${r.label} ${r.key} ${r.type} ${(r.samples ?? []).join(' ')}`,
  limit: 200,
});

function RemoteColumnPicker({ value, options, suggested, disabled, error, onChange }: {
  value: string;
  options: RemoteField[];
  suggested?: string | null;
  disabled?: boolean;
  error?: boolean;
  onChange: (key: string) => void;
}) {
  // A saved key the source no longer describes is still shown as the value,
  // so nobody loses sight of what was mapped.
  const selected = useMemo<RemoteField | null>(() => {
    if (!value) return null;
    return options.find((r) => r.key === value)
      ?? { key: value, label: value, type: '', samples: [], stored_as: null };
  }, [options, value]);
  // The suggested column first, then by name.
  const sorted = useMemo(
    () => [...options].sort((a, b) =>
      Number(b.key === suggested) - Number(a.key === suggested)
      || (a.label || a.key).localeCompare(b.label || b.key, undefined, { sensitivity: 'base' })),
    [options, suggested],
  );
  return (
    <Autocomplete
      size="small" fullWidth autoHighlight openOnFocus
      disabled={disabled}
      options={sorted}
      value={selected}
      onChange={(_e, opt) => onChange(opt?.key ?? '')}
      filterOptions={filterRemote}
      getOptionLabel={(r) => (r.label && r.label !== r.key ? `${r.label} (${r.key})` : r.key)}
      isOptionEqualToValue={(o, v) => o.key === v.key}
      noOptionsText="No column matches. Try part of its name, code or a sample value"
      slotProps={{ listbox: { style: { maxHeight: 320 } } }}
      renderOption={({ key: _key, ...liProps }, r) => (
        <Box component="li" key={r.key} {...liProps} sx={{ display: 'block !important' }}>
          <Box sx={{ fontSize: fs.base, fontWeight: 600 }}>
            {r.label || r.key}
            {suggested === r.key && (
              <Box component="span" sx={{ color: 'info.main', ml: 1, fontSize: fs.small, fontWeight: 400 }}>suggested</Box>
            )}
          </Box>
          <Box sx={{ fontSize: fs.tiny, color: 'text.secondary' }}>
            {r.key}{r.type ? ` · ${r.type}` : ''}
            {r.samples?.length ? ` · e.g. ${r.samples.slice(0, 2).join(', ')}` : ''}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} error={error} placeholder="Not mapped. Search to pick a column" />
      )}
    />
  );
}
